<template>
  <v-dialog
    v-model="internalShow"
    max-width="800"
    scrollable
    :fullscreen="isMobile"
    :z-index="3100"
  >
    <v-card class="policy-modal">
      <v-card-title class="policy-modal__header">
        <div class="policy-modal__header-content">
          <v-icon start icon="mdi-shield-account" color="primary" size="24" />
          <span class="policy-modal__header-title">Privacy Policy</span>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="close"
          aria-label="Close privacy policy"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="policy-modal__content">
        <div class="policy-modal__last-updated">
          Effective Date: September 1, 2025
        </div>

        <p class="policy-modal__intro">
          This Privacy Policy explains how ePongo, Inc. ("ePongo," "we," "our,"
          or "us") collects, uses, shares, and safeguards information when you
          use our websites, platforms, mobile applications, and related services
          (the "Services").
        </p>

        <section class="policy-modal__section">
          <h3>1. Information We Collect</h3>
          <p>We collect the following categories of information:</p>
          <ul>
            <li>
              <strong>Account and Demographic Information</strong> - name,
              email, age, gender, preferences.
            </li>
            <li>
              <strong>Geolocation Data</strong> - IP address, device location,
              and location-based services for targeted advertising and content
              delivery.
            </li>
            <li>
              <strong>Verified Listener Data</strong> - phone number (used for
              SMS verification).
            </li>
            <li>
              <strong>Artist and Contest Data</strong> - addresses, payment
              details (e.g., bank account or PayPal), and information required
              for contests, prize delivery, or merchandise fulfillment.
            </li>
            <li>
              <strong>Usage Data</strong> - device information, browsing
              activity, listening history, playlists, and interactions.
            </li>
          </ul>
        </section>

        <section class="policy-modal__section">
          <h3>2. How We Collect Information</h3>
          <p>
            We collect information directly from you when you register or
            provide details, automatically through cookies, log files, and
            analytics tools, and from third parties used for verification,
            analytics, advertising, or demographic/geolocation enrichment.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>3. How We Use Information</h3>
          <p>We use collected information to:</p>
          <ul>
            <li>Provide and improve the Services</li>
            <li>Verify listener and artist identities</li>
            <li>Process payments, royalties, and contest rewards</li>
            <li>Deliver targeted advertisements and personalized content</li>
            <li>
              Send promotions, newsletters, and marketing communications (with
              opt-out options)
            </li>
            <li>Conduct analytics to improve user experience</li>
            <li>Comply with legal and regulatory obligations</li>
          </ul>
        </section>

        <section class="policy-modal__section">
          <h3>4. Sharing and Disclosure</h3>
          <p>We do not sell your personal data. We may share:</p>
          <ul>
            <li>Anonymous or aggregated data with advertisers and partners.</li>
            <li>
              Required artist and track information with ASCAP, BMI, SESAC, and
              distribution partners to ensure proper reporting and royalty
              processing.
            </li>
            <li>Payment details with secure third-party payment processors.</li>
            <li>
              Information with trusted third-party vendors for SMS verification,
              fraud prevention, and analytics.
            </li>
            <li>
              Information when required by law, regulation, or legal process.
            </li>
          </ul>
        </section>

        <section class="policy-modal__section">
          <h3>5. Data Security</h3>
          <p>
            We use reasonable technical and organizational measures to protect
            information. However, no system is completely secure, and we cannot
            guarantee absolute security of your data.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>6. Data Retention</h3>
          <p>
            We retain personal information as long as necessary to provide the
            Services, comply with legal obligations, resolve disputes, and
            enforce agreements.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>7. Your Rights</h3>
          <p>
            You may request access, correction, or deletion of your personal
            information through your account settings or by contacting us. You
            may opt out of marketing emails at any time. Residents of certain
            jurisdictions (e.g., California, EU) may have additional rights
            under applicable law.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>8. Children's Privacy</h3>
          <p>
            The Services are not intended for children under the age of 13, and
            we do not knowingly collect personal information from children under
            13. If we learn that such data has been collected, we will delete it
            promptly.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>9. Cookies and Tracking</h3>
          <p>
            We use cookies and similar technologies to enable functionality,
            analyze usage, and deliver targeted advertisements. You can manage
            cookies through your browser settings, but disabling cookies may
            limit functionality.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>10. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. Updated
            versions will be posted with a revised Effective Date. Continued use
            of the Services indicates acceptance of the updated policy.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>11. Contact</h3>
          <p>
            If you have questions about this Privacy Policy, please contact us
            at:
            <a href="mailto:privacy@epongo.com" class="policy-modal__link"
              >privacy@epongo.com</a
            >
          </p>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="policy-modal__actions">
        <v-spacer />
        <v-btn color="primary" variant="elevated" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";

interface Props {
  modelValue: boolean;
}

interface Emits {
  (e: "update:modelValue", value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { mobile: isMobile } = useDisplay();

const internalShow = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

function close() {
  internalShow.value = false;
}
</script>

<style scoped lang="scss">
.policy-modal {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    padding-top: calc(20px + var(--safe-area-top));
    background: rgba(var(--v-theme-on-surface), 0.02);
  }

  &__header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  &__header-title {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  &__content {
    padding: 24px;
    line-height: 1.6;
  }

  &__last-updated {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-bottom: 24px;
    padding: 8px 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 8px;
  }

  &__intro {
    color: rgba(var(--v-theme-on-surface), 0.7);
    margin-bottom: 16px;
  }

  &__section {
    margin-bottom: 32px;

    &:last-child {
      margin-bottom: 0;
    }

    h3 {
      font-family: var(--font-display);
      font-size: 1.125rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #f59e0b;
    }

    h4 {
      font-size: 1rem;
      font-weight: 500;
      color: rgba(var(--v-theme-on-surface), 0.5);
      margin: 16px 0 8px;
    }

    p {
      color: rgba(var(--v-theme-on-surface), 0.7);
      margin-bottom: 12px;
    }

    ul {
      margin: 12px 0;
      padding-left: 20px;

      li {
        color: rgba(var(--v-theme-on-surface), 0.7);
        margin-bottom: 6px;
      }
    }
  }

  &__link {
    color: #f59e0b;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__actions {
    padding: 16px 24px;
    padding-bottom: calc(16px + var(--safe-area-bottom));
  }
}

// Light theme
.v-theme--light {
  .policy-modal {
    background: rgb(var(--v-theme-surface));
    border-color: rgba(var(--v-theme-on-surface), 0.08);

    &__header {
      background: rgba(var(--v-theme-on-surface), 0.02);
    }

    &__header-title {
      color: rgb(var(--v-theme-on-surface));
    }

    &__last-updated {
      color: rgba(var(--v-theme-on-surface), 0.4);
      background: rgba(var(--v-theme-on-surface), 0.03);
    }

    &__intro {
      color: #44403c;
    }

    &__section {
      h3 {
        color: rgb(var(--v-theme-on-surface));
        border-bottom-color: #d97706;
      }

      h4 {
        color: rgba(var(--v-theme-on-surface), 0.4);
      }

      p,
      li {
        color: #44403c;
      }
    }

    &__link {
      color: #d97706;
    }
  }
}

@media (max-width: 599px) {
  .policy-modal {
    &__header {
      padding: 16px 20px;
    }

    &__header-title {
      font-size: 1.125rem;
    }

    &__content {
      padding: 20px;
    }

    &__section {
      margin-bottom: 24px;

      h3 {
        font-size: 1rem;
      }

      h4 {
        font-size: 0.9375rem;
      }
    }
  }
}
</style>
